import { useCallback } from 'react';
import type { NavigateFunction } from 'react-router-dom';
import type { Incident, IncidentGroup } from '../../services/incidentsApi';
import type { SafetyDashboardRow } from '../../services/reportsApi';
import type { RiskPatternWorkflowReviewContext } from '../../domain/incidents/riskPatternWorkflowReviewContext';
import {
  buildProofPackageHandoffFromReviewSource,
} from '../../domain/safetyCases/safetyScanWorkspaceHandoff';
import { readAcceptanceProofCache } from '../../utils/acceptanceArtifacts';
import { resolveSafetyCaseId } from '../../utils/acceptanceHierarchyNavigation';
import { buildArtifactFlowPath } from '../../utils/reportFlow';
import {
  resolvePersistedSafetyReportIdForIncidentContext,
  resolveSafetyReportOpenPath,
} from '../../utils/safetyReportOpenNavigation';
import { safetyScanVerifyPath } from '../../domain/acceptance/scanVerifyNavigation';
import { toLocalInputValue } from './useIncidentsTimeRange';

export function useIncidentsProofPackageNavigation(args: {
  canOpenArtifactEntry: boolean;
  navigate: NavigateFunction;
  searchParams: URLSearchParams;
  selectedGroupContext: IncidentGroup | null;
  selectedIncident: Incident | null;
  filterDeviceId: string;
  filterZoneId: string;
  filterCameraId: string;
  filterIncidentType: string;
  timeRange: { fromDate?: string; toDate?: string };
  riskPatternWorkflowDashboardRow: SafetyDashboardRow | null;
  riskPatternWorkflowReviewContext: RiskPatternWorkflowReviewContext | null;
  getScopedOrgId: () => string;
  getScopedSiteId: () => string;
}) {
  const {
    canOpenArtifactEntry,
    navigate,
    searchParams,
    selectedGroupContext,
    selectedIncident,
    filterDeviceId,
    filterZoneId,
    filterCameraId,
    filterIncidentType,
    timeRange,
    riskPatternWorkflowDashboardRow,
    riskPatternWorkflowReviewContext,
    getScopedOrgId,
    getScopedSiteId,
  } = args;

  return useCallback(() => {
    if (!canOpenArtifactEntry) return;
    const reviewSource: any = selectedGroupContext || selectedIncident || null;
    const proofHandoff = buildProofPackageHandoffFromReviewSource({
      reviewSource,
      selectedIncident,
      searchParams,
      dashboardRow: riskPatternWorkflowDashboardRow,
    });
    const workflowId = String(
      riskPatternWorkflowReviewContext?.workflowId
      || riskPatternWorkflowDashboardRow?.workflow_id
      || proofHandoff.workflowId
      || '',
    ).trim();
    const safetyCaseId = String(
      riskPatternWorkflowReviewContext?.safetyCaseId
      || resolveSafetyCaseId(riskPatternWorkflowDashboardRow)
      || proofHandoff.safetyCaseId
      || '',
    ).trim();
    const representativeIncidentId = String(
      proofHandoff.representativeIncidentId
      || riskPatternWorkflowReviewContext?.representativeIncidentId
      || reviewSource?.linked_detailed_record_id
      || reviewSource?.incident_id
      || selectedIncident?.incident_id
      || '',
    ).trim();
    const reportFrom = toLocalInputValue(timeRange.fromDate || '');
    const reportTo = toLocalInputValue(timeRange.toDate || '');

    const openSafetyScanFromHandoff = async () => {
      const persistedScanId = String(
        riskPatternWorkflowDashboardRow?.active_draft_report_id
        || '',
      ).trim();
      if (!persistedScanId) {
        throw new Error('No persisted Safety Scan to open');
      }
      navigate(safetyScanVerifyPath(persistedScanId, {
        orgId: getScopedOrgId(),
        siteId: getScopedSiteId(),
        safetyCaseId: safetyCaseId || null,
        incidentId: representativeIncidentId || null,
      }));
    };

    const openLegacyProofPackagePath = (persistedReportId = '') => {
      const anchorIncidentId = representativeIncidentId;
      const policyId = String(
        reviewSource?.policy_id || reviewSource?.triggered_by?.policy_id || selectedIncident?.policy_id || selectedIncident?.triggered_by?.policy_id || '',
      ).trim();
      const policyName = String(
        reviewSource?.policy_name || reviewSource?.triggered_by?.policy_name || selectedIncident?.policy_name || selectedIncident?.triggered_by?.policy_name || '',
      ).trim();
      const policyType = String(reviewSource?.incident_type || selectedIncident?.incident_type || filterIncidentType || '').trim();
      const includedIncidentCases = Number(
        reviewSource?.alert_count || reviewSource?.raw?.alert_count || selectedIncident?.raw?.alert_count || 0,
      );
      const deviceId = String(reviewSource?.device_id || filterDeviceId || '').trim() || undefined;
      const fallbackPath = buildArtifactFlowPath({
        kind: 'proof_pack',
        task: 'investigate_incident',
        orgId: getScopedOrgId(),
        siteId: getScopedSiteId(),
        deviceId,
        zoneId: String(reviewSource?.zone_id || filterZoneId || '').trim() || undefined,
        cameraId: String(reviewSource?.camera_id || filterCameraId || '').trim() || undefined,
        reportFrom,
        reportTo,
        caseId: anchorIncidentId || undefined,
        incidentId: anchorIncidentId || undefined,
        workflowId: workflowId || undefined,
        safetyCaseId: safetyCaseId || undefined,
        policyId: policyId || undefined,
        policyType: policyType || undefined,
        policyName: policyName || undefined,
        includedIncidentCases: includedIncidentCases > 0 ? includedIncidentCases : undefined,
      });
      const scopedOrgId = getScopedOrgId();
      const scopedSiteId = getScopedSiteId();
      const devicePreview = readAcceptanceProofCache(scopedOrgId, scopedSiteId, deviceId);
      const sitePreview = readAcceptanceProofCache(scopedOrgId, scopedSiteId);
      const resolvedReportId = persistedReportId
        || resolvePersistedSafetyReportIdForIncidentContext({
          proofPreview: devicePreview,
          anchorIncidentId,
          groupKey: String(reviewSource?.group_key || workflowId || '').trim(),
        })
        || resolvePersistedSafetyReportIdForIncidentContext({
          proofPreview: sitePreview,
          anchorIncidentId,
          groupKey: String(reviewSource?.group_key || workflowId || '').trim(),
        });
      void resolveSafetyReportOpenPath({
        fallbackPath,
        persistedReportId: resolvedReportId,
      }).then((nextPath) => navigate(nextPath));
    };

    if (workflowId && (safetyCaseId || representativeIncidentId)) {
      void openSafetyScanFromHandoff().catch(() => openLegacyProofPackagePath());
      return;
    }

    openLegacyProofPackagePath();
  }, [
    canOpenArtifactEntry,
    filterCameraId,
    filterDeviceId,
    filterIncidentType,
    filterZoneId,
    getScopedOrgId,
    getScopedSiteId,
    navigate,
    riskPatternWorkflowDashboardRow,
    riskPatternWorkflowReviewContext,
    searchParams,
    selectedGroupContext,
    selectedIncident,
    timeRange.fromDate,
    timeRange.toDate,
  ]);
}
